import React, {useMemo} from 'react';
import {useAgentBuilderStore} from './AgentBuilder.state';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Text,
} from '@chakra-ui/react';
import {Divider, Flex} from '@chakra-ui/react';
import {AgentType, AgentTypeLabels} from '../../../shared/agent';
import Select from 'react-select';
import {defaultSelectStyles} from '../../theme/select';
import {BuilderCenteredContent} from './BuilderCenteredContent';
import {BuilderFooter} from './BuilderFooter';
import {AgentTypeInfo, useAvailableAgentTypes} from './agentTypeInfo';
import {useAgentNavigation} from './AgentBuilderNav.state';
import {AgentBuilderPageIndicator} from './AgentBuilderPageIndicator';
import {
  LabelAndDescriptionData,
  LabelAndDescriptionOption,
} from '../../LabelAndDescriptionOption';
import {StepContainer} from './StepContainer';
import {AgentLabel} from '../../../shared/validation';

export const AgentBuilderSetNameStep = () => {
  const {type, isSaveable, saveChanges} = useAgentBuilderStore();

  const onNavigate = useAgentNavigation({
    mutation: saveChanges,
    saveOnBack: isSaveable(),
  });

  return (
    <StepContainer isSaveable={isSaveable()} onSaveMutation={saveChanges}>
      <Flex direction="column" flex="1">
        <BuilderCenteredContent>
          <AgentBuilderPageIndicator />
          <Flex direction="column" gap="2">
            <Text fontSize="xl">
              Say hello to your new {type ? AgentTypeLabels[type] : 'agent'}
            </Text>
            <Text color="kgray.300">
              We recommend giving your agent a descriptive name based on the job
              you want it to do.
            </Text>
          </Flex>
          <Flex direction="column" gap="6" mt="8">
            <AgentEditorSection />
          </Flex>
        </BuilderCenteredContent>

        <Divider />

        <BuilderFooter justifyContent="end">
          <Button
            colorScheme="kbuttonblue"
            isDisabled={!isSaveable()}
            isLoading={saveChanges.isPending}
            onClick={() => onNavigate('next')}
          >
            Continue
          </Button>
        </BuilderFooter>
      </Flex>
    </StepContainer>
  );
};

type AgentTypeOption = LabelAndDescriptionData<AgentType>;
const agentTypeSelectStyles = defaultSelectStyles<AgentTypeOption, false>();

const AgentEditorSection = () => {
  const {label, type, setLabel, setType, serverState} = useAgentBuilderStore();
  const availableAgentTypes = useAvailableAgentTypes({
    currentType: type,
    filter: 'all',
  });

  const agentTypeOptions = useMemo(
    () =>
      availableAgentTypes.map((type) => ({
        label: AgentTypeLabels[type],
        description: AgentTypeInfo[type].description,
        value: type,
      })),
    [availableAgentTypes]
  );

  return (
    <>
      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="normal" textTransform="uppercase">
          Agent name
        </FormLabel>
        <Input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Pricing Strategy"
        />
        <FormHelperText color="kgray.300">
          Agent name must be between {AgentLabel.minLength} and{' '}
          {AgentLabel.maxLength} characters.
        </FormHelperText>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="normal" textTransform="uppercase">
          Agent type
        </FormLabel>
        <Select
          components={{Option: LabelAndDescriptionOption<AgentType>}}
          isDisabled={!!serverState}
          isMulti={false}
          isSearchable={false}
          menuPlacement="auto"
          onChange={(newValue) => {
            if (!newValue) {
              return;
            }

            setType(newValue.value);
          }}
          options={agentTypeOptions}
          styles={agentTypeSelectStyles}
          value={agentTypeOptions.find((option) => option.value === type)}
        />
      </FormControl>
    </>
  );
};
